import {
  EditOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Input, Popconfirm } from 'antd'
import { sortBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import { ReportConfigurationModule, ResourceType } from '@/schemas'

import { ReportConfigurationModuleFieldContainer } from './report-configurarion-module-field.component'

interface ReportConfigurationModuleProps {
  selfId: string
  type: ResourceType
  module: ReportConfigurationModule
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  refreshConfig: () => void
}

export const ReportConfigurationModuleContainer: React.FC<ReportConfigurationModuleProps> = ({
  selfId,
  type,
  module,
  canCreate,
  canUpdate,
  canDelete,
  refreshConfig,
}) => {
  const { t } = useTranslation()
  const [isSetName, setIsSetName] = React.useState(false)

  const { run: upsertModule } = useRequest(
    (data: ReportConfigurationModule) => ({
      url: '/api/report/upsertReportConfigModule',
      method: 'post',
      data: {
        selfId,
        type,
        ...data,
      },
    }),
    {
      manual: true,
      throwOnError: true,
      onSuccess: () => refreshConfig(),
    }
  )

  const removeModule = () => {
    if (module) {
      upsertModule({
        ...module,
        isDeleted: true,
      })
    }
  }

  const toggleModule = () => {
    if (module) {
      const { isChecked } = module
      upsertModule({
        ...module,
        isChecked: !isChecked,
      })
    }
  }

  const changeModuleName = (value: string) => {
    if (value !== module.name) {
      upsertModule({
        ...module,
        name: value,
      })
    }
    setIsSetName(false)
  }

  const createField = () => {
    const field = {
      id: v4(),
      name: '自定义模块',
      order: null,
      isDefault: false,
      isChecked: true,
      isDeleted: false,
      moduleId: module.id,
    }
    upsertModule({
      ...module,
      fields: module.fields.concat([field]),
    })
  }

  return (
    <div
      className={`flex flex-col space-y-10px ${type !== ResourceType.PROJECT ? 'report-config-modules-container' : ''}`}
    >
      {type !== ResourceType.PROJECT && (
        <div className='report-config-modules-container-header flex justify-between'>
          <div className='flex items-center'>
            {isSetName && (
              <Input
                autoFocus
                defaultValue={t(module.name) as string}
                onBlur={(e) => changeModuleName(e.target.value)}
                onPressEnter={(e) => changeModuleName(e.target['value'])}
              />
            )}
            {!isSetName && (
              <>
                {t(module.name)}
                <EditOutlined
                  className={`ml-5px ${canUpdate ? 'cursor-pointer' : '!cursor-not-allowed'}`}
                  onClick={() => canUpdate && setIsSetName(true)}
                />
              </>
            )}
          </div>
          <div>
            {!module.isDefault && (
              <Popconfirm
                title={t('当前操作会删除当前已修改模块的内容')}
                onConfirm={removeModule}
                okText={t('remove')}
                cancelText={t('cancel')}
                disabled={!canDelete}
              >
                <span className={`${canDelete ? 'cursor-pointer' : '!cursor-not-allowed'}`}>
                  {t('移除集合')}
                  <MinusCircleOutlined className='ml-5px' />
                </span>
              </Popconfirm>
            )}
            {module.isDefault && module.isChecked && !module.isExtra && (
              <span
                className={`${canUpdate ? 'cursor-pointer' : '!cursor-not-allowed'}`}
                onClick={() => canUpdate && toggleModule()}
              >
                {t('隐藏')} <EyeInvisibleOutlined />
              </span>
            )}
          </div>
        </div>
      )}
      {sortBy(
        module.fields.filter((f) => !f.isDeleted),
        ['order', 'createdAt']
      ).map((field) => (
        <ReportConfigurationModuleFieldContainer
          key={field.id}
          selfId={selfId}
          type={type}
          module={module}
          field={field}
          refreshConfig={refreshConfig}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      ))}
      {module.isExtra && (
        <Button disabled={!canCreate} type='text' className='flex items-center pl-10px !mb-10px text-blue-500'>
          <span className='cursor-pointer' onClick={() => createField()}>
            <PlusOutlined className='mr-5px' />
            {t('自定义模块')}
          </span>
        </Button>
      )}
      {!module.isExtra && (
        <div className='h-20px flex items-center pl-20px !mb-10px'>
          <InfoCircleOutlined className='mr-5px' />
          {t('请在周报内容中调整数据来源')}
        </div>
      )}
    </div>
  )
}
